"use client";

import { mediaSrc } from "../../../_lib/api";
import { useProducts } from "../../../_lib/hooks";
import { SITE_URL } from "../../../_lib/site";
import { DEFAULT_SETTINGS } from "../../../_lib/siteConfig";
import { useSite } from "../../../_lib/useSite";
import { Zone } from "../blocks/Zone";
import { CardArt } from "./CardArt";
import { IconCheck } from "./parts";

/*
 * PC 랜딩 — 시안 15 (`docs/design/simple-skin/sample/15-landing-pc.html`). `pcLanding` 이 켜진 사이트의
 * PC 폭(lg 이상)에서만 보입니다(`../screens/Home.tsx` 가 CSS 로 가릅니다).
 *
 * 🔴 **템플릿 구조를 쓰지 않습니다**(결정 기록 §7) — «이용 방법 ①②③» 카드 대신 실제 화면 그림,
 *    균일한 격자 대신 **가로로 넘치는 카드 줄**, 히어로 사진은 마스크로 흘려 한 덩어리 느낌을 없앱니다.
 * 🔴 **히어로 사진은 `loading="lazy"`** — 모바일 폭에서는 이 블록이 `display: none` 이라 받지 않습니다.
 *    관리자가 올린 사진(`settings.heroUrl`)이 없으면 몰의 기본 사진(`/images/simple/hero.jpg`).
 * 🔴 **취급 상품권은 실제 상품**(`useProducts`)입니다 — 시안의 «8종 · 액면가 판매 · 유효기간 5년» 같은
 *    주장은 사이트마다 다르고 코드가 확인할 수 없어 개수만 적습니다(`docs/base/11` §7-3).
 * 🔑 서비스 주소는 `SITE_URL` 의 호스트입니다(«〈서비스 주소〉» 자리 · 휴대폰으로 접속하라는 안내).
 * 🆕 큰 제목·보조 문장·상품 줄 제목은 관리자 문구(`settings.copyLanding*` · `docs/base/12` §3)이고,
 *    상품 줄 아래(`landing.middle`)와 맨 아래(`landing.bottom`)에 블록 자리가 있습니다 — 폭은 본문과 같은 1120.
 */
export function Landing() {
  const site = useSite();
  const { brand } = site;
  const copy = site.skin === "simple" ? site.settings : DEFAULT_SETTINGS.simple;
  const products = useProducts();
  const heroSrc = (site.skin === "simple" ? mediaSrc(site.settings.heroUrl) : null) ?? "/images/simple/hero.jpg";
  const host = SITE_URL.replace(/^https?:\/\//, "");
  const items = products.data ?? [];
  const sample = items[0];

  return (
    <div className="bg-surface">
      <section className="relative overflow-hidden bg-ground" aria-labelledby="landing-title">
        {/* eslint-disable-next-line @next/next/no-img-element -- 관리자 업로드 이미지(BFF 경유) 또는 정적 기본 사진 */}
        <img
          src={heroSrc}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute top-0 -right-10 h-full w-auto max-w-none object-cover [mask-image:linear-gradient(to_right,transparent_0%,#000_26%)]"
        />
        <div className="relative mx-auto w-full max-w-[1120px] px-10 pt-24 pb-[104px] xl:px-0">
          <p className="mb-[18px] text-[length:calc(var(--tnf-text-base)*14.5/14)] font-bold tracking-[0.12em] text-ink-faint">{brand.tagline}</p>
          {/* 🔄 큰 제목을 비우면 화면에서 빠집니다 — 낭독기용으로 서비스명을 숨겨 둡니다(h1 이 없는 화면이 되지 않게). */}
          <h1
            id="landing-title"
            className={copy.copyLandingTitle ? "max-w-[560px] text-[length:calc(var(--tnf-text-base)*62/14)] leading-[1.22] font-bold tracking-[-0.045em] text-ink" : "sr-only"}
          >
            {(copy.copyLandingTitle || brand.siteName).split("\n").map((line, i) => (
              <span key={i} className="block">
                {line}
              </span>
            ))}
          </h1>
          {copy.copyLandingLead && <p className="mt-[26px] text-[length:calc(var(--tnf-text-base)*18/14)] text-ink-soft">{copy.copyLandingLead}</p>}
          <div className="mt-[34px] inline-flex items-center gap-3.5 rounded-[var(--tnf-radius-lg)] bg-surface py-2.5 pr-3 pl-[22px] shadow-[0_2px_4px_rgb(25_31_40_/_6%),0_14px_34px_-22px_rgb(25_31_40_/_30%)]">
            <span className="text-subtitle font-bold tracking-[0.01em] text-ink">{host}</span>
            <span aria-hidden="true" className="h-[22px] w-px bg-line" />
            <span className="text-body text-ink-faint">휴대폰으로 접속하세요</span>
          </div>
        </div>
      </section>

      {items.length > 0 && (
        <section className="pt-[76px] pb-16" {...(copy.copyLandingProducts ? { "aria-labelledby": "landing-products" } : { "aria-label": "취급 상품권" })}>
          {/* 🔄 줄 제목을 비우면 제목 줄(개수 포함)이 빠지고 상품 줄만 남습니다. */}
          {copy.copyLandingProducts && (
            <div className="mx-auto mb-[30px] flex w-full max-w-[1120px] items-baseline gap-3.5 px-10 xl:px-0">
              <h2 id="landing-products" className="text-[length:calc(var(--tnf-text-base)*28/14)] font-bold tracking-[-0.035em] text-ink">{copy.copyLandingProducts}</h2>
              <span className="text-lead text-ink-faint">{items.length}종</span>
            </div>
          )}
          {/* 🔑 가로로 넘치는 줄 — 페이지가 아니라 이 줄만 옆으로 밉니다(가로 넘침 0). */}
          <ul className="flex items-end gap-[18px] overflow-x-auto pr-10 pb-2 pl-10 xl:pl-[calc((100%-1120px)/2)]">
            {items.map((p, index) => (
              <li key={p.id} className="flex-none">
                {index === 0 ? (
                  <CardArt name={p.name} brand={p.brandName} width={240} height={150} big />
                ) : (
                  <CardArt name={p.name} brand={p.brandName} width={200} height={126} big />
                )}
                <div className={`mt-[13px] font-bold tracking-[-0.015em] text-ink ${index === 0 ? "text-subtitle" : "text-[length:calc(var(--tnf-text-base)*15.5/14)]"}`}>{p.name}</div>
                <div className={`mt-[3px] text-ink-soft tabular-nums ${index === 0 ? "text-lead" : "text-body"}`}>{p.salePrice.toLocaleString("ko-KR")}원</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Zone name="landing.middle" className="mx-auto mb-16 w-full max-w-[1120px] px-10 xl:px-0" />

      <section className="bg-ground pt-[76px] pb-[84px]" aria-labelledby="landing-screens">
        <div className="mx-auto w-full max-w-[1120px] px-10 xl:px-0">
          <h2 id="landing-screens" className="mb-10 text-[length:calc(var(--tnf-text-base)*28/14)] font-bold tracking-[-0.035em] text-ink">구매 화면</h2>
          {/* 🔑 실제 화면의 그림입니다 — 장식이라 낭독기에는 숨깁니다(제목이 뜻을 말합니다). */}
          <div aria-hidden="true" className="flex items-center gap-[26px] pl-2">
            <Phone name={brand.siteName} tilt="-rotate-3">
              <MiniCard>
                <MiniSlot label="상품권">상품권을 선택하세요</MiniSlot>
                <MiniSlot label="받는 사람">010-0000-0000</MiniSlot>
                <div className="h-9 rounded-[var(--tnf-radius-xs)] bg-line" />
              </MiniCard>
            </Phone>
            <Phone name={brand.siteName} tilt="rotate-2">
              <MiniCard>
                <MiniSlot label="상품권" strong>{sample ? `${sample.name} ${sample.salePrice.toLocaleString("ko-KR")}원` : "상품권"}</MiniSlot>
                <MiniSlot label="받는 사람" strong>010-2345-6789</MiniSlot>
                <div className="flex h-9 items-center justify-center rounded-[var(--tnf-radius-xs)] bg-accent text-[length:calc(var(--tnf-text-base)*12/14)] font-bold text-on-accent">구매하기</div>
              </MiniCard>
            </Phone>
            <Phone name={brand.siteName} tilt="-rotate-2">
              <div className="rounded-[var(--tnf-radius-md)] bg-surface px-2.5 py-3.5 text-center">
                <span className="inline-grid size-[34px] place-items-center rounded-full bg-good-wash text-good"><IconCheck size={18} width={2.6} /></span>
                <div className="mt-2.5 text-[length:calc(var(--tnf-text-base)*12.5/14)] font-bold">발급 완료</div>
                <div className="mt-2 rounded-[var(--tnf-radius-xs)] bg-raised p-[9px]">
                  <div className="text-[length:calc(var(--tnf-text-base)*9/14)] text-ink-faint">핀번호</div>
                  <div className="mt-1 text-[length:calc(var(--tnf-text-base)*11.5/14)] font-bold tracking-[0.05em] tabular-nums">3812 4470 9126 5503</div>
                </div>
              </div>
            </Phone>
          </div>
        </div>
      </section>

      <Zone name="landing.bottom" className="mx-auto w-full max-w-[1120px] px-10 py-16 xl:px-0" />
    </div>
  );
}

function Phone({ name, tilt, children }: { name: string; tilt: string; children: React.ReactNode }) {
  return (
    <div className={`w-[216px] flex-none rounded-[var(--tnf-radius-phone)] bg-ink p-2 shadow-[0_22px_44px_-26px_rgb(15_20_28_/_50%)] ${tilt}`}>
      <div className="overflow-hidden rounded-[var(--tnf-radius-phone-in)] bg-ground text-ink">
        <div className="flex h-[34px] items-center border-b border-line bg-surface px-[11px] text-[length:calc(var(--tnf-text-base)*10.5/14)] font-bold">{name}</div>
        <div className="p-[11px]">{children}</div>
      </div>
    </div>
  );
}

function MiniCard({ children }: { children: React.ReactNode }) {
  return <div className="rounded-[var(--tnf-radius-md)] bg-surface p-2.5">{children}</div>;
}

function MiniSlot({ label, strong = false, children }: { label: string; strong?: boolean; children: React.ReactNode }) {
  return (
    <div className="mb-[7px] rounded-[var(--tnf-radius-xs)] bg-raised px-2.5 py-[9px]">
      <div className="text-[length:calc(var(--tnf-text-base)*9.5/14)] text-ink-faint">{label}</div>
      <div className={`mt-[5px] truncate ${strong ? "text-[length:calc(var(--tnf-text-base)*13/14)] font-bold" : "text-[length:calc(var(--tnf-text-base)*12/14)] font-medium"}`}>{children}</div>
    </div>
  );
}
